import HiddenInput from "@/components/form/components/HiddenInput";
import { phoneNumberNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Alert, Checkbox, Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAdminUpdateUser } from "../../../../common/api/queries";
import { MOBILE_NUMBER_COUNTRY_WHITELIST, ModalSizes, rowGutter } from "../../../../common/constants";
import { useIsSystemAdmin } from "../../../auth/hooks";
import { userChanged } from "../../../auth/session";
import { AdminUpdateUser, UserAdminView } from "../../types";
import { hasConfirmedAgentUserAccount, hasConfirmedClientUserAccount } from "../../utils";

interface Props {
  open: boolean;
  user?: UserAdminView;
  onFormCancel: () => void;
}

export const AdminUpdateUserForm = ({ open, user, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const isSystemAdmin = useIsSystemAdmin();

  const [form] = Form.useForm<AdminUpdateUser>();
  const locked = Form.useWatch("locked", form);

  const adminUpdateUser = useAdminUpdateUser();

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        optimisticLockVersion: user.optimisticLockVersion,
        email: user.emailToConfirm,
        phone: user.phoneToConfirm,
        name: user.name,
        locked: user.locked,
        systemAdmin: user.agentUserRole?.systemAdmin,
      });
    }
  }, [open, user, form]);

  const handleFormSubmit = () => {
    if (user) {
      form
        .validateFields()
        .then((values) => {
          adminUpdateUser.mutate(
            { id: user.id, object: values },
            {
              onSuccess: (updatedUser) => {
                userChanged(updatedUser.id);

                onFormCancel();
              },
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        })
        .catch(resolveFormValidationError);
    }
  };

  const colSpan = 8;

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t("user.titles.updateUser")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={adminUpdateUser.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="userUpdateForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="name"
              label={t("user.attrs.name")}
              rules={[validations.notNull, validations.size(1, 255), validations.pattern(regexPatterns.nameRegex)]}
            >
              <Input placeholder={t("user.helpers.userNamePlaceholder")} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="locked"
              className="form-item-without-label"
              valuePropName="checked"
              initialValue={false}
              rules={[validations.none]}
            >
              <Checkbox disabled={!locked}>{t("user.attrs.locked")}</Checkbox>
            </Form.Item>
          </Col>

          {isSystemAdmin && (
            <Col span={colSpan}>
              <Form.Item
                name="systemAdmin"
                className="form-item-without-label"
                valuePropName="checked"
                initialValue={false}
                rules={[validations.none]}
              >
                <Checkbox>{t("user.attrs.systemAdmin")}</Checkbox>
              </Form.Item>
            </Col>
          )}
        </Row>

        {user && !hasConfirmedAgentUserAccount(user) && !hasConfirmedClientUserAccount(user) && (
          <>
            <Row gutter={rowGutter}>
              <Col span={colSpan}>
                <Form.Item
                  name="email"
                  label={t("user.attrs.email")}
                  rules={[validations.notBlank, validations.size(1, 254), validations.email]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={colSpan}>
                <Form.Item
                  name="phone"
                  label={t("user.attrs.phone")}
                  rules={[validations.notBlank, validations.mobilePhoneNumber(MOBILE_NUMBER_COUNTRY_WHITELIST)]}
                  normalize={phoneNumberNormalizeFunction}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={colSpan * 3}>
                <Alert
                  className="margin-top-medium"
                  title={t("user.helpers.updateUnconfirmedUserInfo")}
                  type="info"
                  showIcon
                />
              </Col>
            </Row>
          </>
        )}
      </Form>

      {user && hasConfirmedAgentUserAccount(user) && (
        <Alert title={t("user.helpers.updateInfo")} type="info" showIcon />
      )}
    </Modal>
  );
};
